/* Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial */
import { useEffect, type FC } from "react";
import type { Session } from "../ClientContext";

export const StaffSessionBridge: FC = () => {
  useEffect(() => {
    const admin = import.meta.env.VITE_ADMIN_URL,
      guest = import.meta.env.VITE_GUEST_URL;
    if (
      !admin ||
      !guest ||
      window.parent === window ||
      window.location.origin !== new URL(admin).origin
    )
      return;
    const origin = new URL(guest).origin,
      controller = new AbortController();
    let busy = false;
    const message = async (event: MessageEvent): Promise<void> => {
      if (
        busy ||
        event.origin !== origin ||
        event.source !== window.parent ||
        event.data?.type !== "call-staff-check" ||
        typeof event.data.nonce !== "string" ||
        event.data.nonce.length > 128
      )
        return;
      busy = true;
      let available = false;
      try {
        const raw = localStorage.getItem("matrix-auth-store"),
          session = raw ? (JSON.parse(raw) as Session) : null;
        if (session?.access_token) {
          const response = await fetch("/api/me", {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
          });
          if (response.ok) {
            const me = await response.json();
            available =
              me.userId === session.user_id &&
              ["owner", "organizer"].includes(me.role);
          }
        }
      } catch {
        /* No valid cabinet session: normal guest entry remains available. */
      }
      if (!controller.signal.aborted)
        window.parent.postMessage(
          { type: "call-staff-result", nonce: event.data.nonce, available },
          origin,
        );
      busy = false;
    };
    const listener = (event: MessageEvent): void => {
      void message(event);
    };
    window.addEventListener("message", listener);
    window.parent.postMessage({ type: "call-staff-ready" }, origin);
    return () => {
      controller.abort();
      window.removeEventListener("message", listener);
    };
  }, []);
  return null;
};
